/**
 * Shared Sentry configuration for CharityStack monorepo
 * Mirrors PostHog architecture with triple-fallback context passing
 *
 * Architecture:
 * - Single Sentry project with app-level tagging
 * - Environment-based configuration (production/development)
 * - Cross-app flow tracking via sessionStorage + URL params
 * - PostHog session linking for analytics correlation
 * - Bot detection: drops bot events in beforeSend to reduce noise/quota
 *
 * Performance Note:
 * - sessionStorage reads in beforeSend are ~0.01ms (negligible)
 * - beforeSend called only on errors (100-1000x per day per user max)
 * - Fresh reads ensure accuracy when PostHog session changes
 * - Bot signal collection runs once at init (~0.1ms, no per-event cost)
 */

import { collectBotSignals, isBotLikely } from './bot-signals.js';

/**
 * Get Sentry configuration for a specific app
 * @param {string} appName - Application name (dashboard, donation-checkout, donation-elements, hosted-page)
 * @param {object} options - Integration options (browserTracingIntegration, replayIntegration)
 * @returns {object} Sentry configuration object
 */
export const getSentryConfig = (appName, options = {}) => {
  // Collect bot signals once per app init (idempotent — safe to call multiple times)
  collectBotSignals();
  const isDevelopment = import.meta.env.VITE_APP_ENVIRONMENT === 'development';

  return {
    environment: import.meta.env.VITE_APP_ENVIRONMENT || 'development',
    release: import.meta.env.VITE_APP_VERSION || '1.0.0-dev',

    // Disable Sentry in development to reduce noise and quota usage
    enabled: !isDevelopment,

    // Ignore third-party errors that are non-actionable
    ignoreErrors: [
      // Third-party tracking scripts (external, non-critical)
      'TrackerStorageType is not defined',
      /TrackerStorageType/,
      /ct\.pinterest\.com/,
      /Failed to fetch.*pinterest/i,
      // Dynamic import failures for non-critical modules
      /Failed to fetch dynamically imported module.*web-vitals/,
      // Network errors for analytics (non-critical)
      /NetworkError.*analytics/i,
      // NOTE: ChunkLoadError was previously ignored here but REMOVED (2026-03-02).
      // ChunkLoadErrors indicate lazy-loaded React components (EmbedForm, EventForm, etc.)
      // failed to download. This causes the "Unable to load donation form" fallback UI.
      // These errors MUST be captured by Sentry so we can diagnose form loading failures.
      // The fix is retry logic in App.jsx lazy() imports, not silencing the error.
      // Browser extension noise
      /ResizeObserver loop/,
      // Browser extension interference with Sentry Replay property setters
      // (e.g., obfuscated extensions running instanceof checks on patched DOM setters)
      /Right-hand side of 'instanceof' is not an object/,

      // Third-party payment provider errors (non-actionable, no stacktrace)
      // These originate from Finix/Stripe/PayPal SDKs, not our code.
      // They're rejected promise values (strings), not proper Error objects.
      // Sentry issues: CHARITYSTACK-MONOREPO-DM, DP
      /Non-Error promise rejection captured with value: Timeout/,
      'Non-Error promise rejection captured with value: Timeout',
      /Non-Error promise rejection captured with value: Timeout \(.\)/,  // Matches "Timeout (b)", etc.

      // WebCrypto API errors from payment SDKs in restrictive browsers
      // Common in Facebook/Instagram in-app browsers with crypto restrictions.
      // Sentry issue: CHARITYSTACK-MONOREPO-DN
      'OperationError: Non-recoverable error. Do not retry.',
      /OperationError.*Non-recoverable error/,

      // Finix SDK internal feature registry errors
      // Occurs during Finix.Auth() initialization — internal features like
      // "webCompat" or "pageContext" are referenced but missing from the SDK's
      // feature registry. Uses varying quote styles (single quotes, backticks).
      // Non-actionable — Finix SDK internal bug, no user-facing impact.
      // Auth succeeds and payments process normally despite these errors.
      // Sentry issues: CHARITYSTACK-MONOREPO-DM, CHARITYSTACK-MONOREPO-JJ
      /feature named ['"`].+['"`] was not found/,

      // Generic third-party script timeout patterns
      // Payment SDKs use raw string rejections for timeouts
      /^Timeout$/,
      /^Timeout \(.\)$/,

      // PostHog session recording initialization race condition
      // Occurs when recording is started before PostHog's recorder script fully loads.
      // Non-actionable - only affects analytics, not user functionality.
      // Sentry issues: CHARITYSTACK-MONOREPO-EP, EQ, ER
      'Called on script loaded before session recording is available',
      /Called on script loaded before session recording is available/,

      // TikTok in-app browser postMessage errors
      // TikTok's browser sends non-standard message events that crash our code.
      // Non-actionable - only affects TikTok in-app browser users, analytics only.
      // Sentry issue: CHARITYSTACK-MONOREPO-E3
      /undefined is not an object \(evaluating 'n\.cid'\)/,

      // PayPal SDK window close errors
      // Occurs when user closes popup after completing payment (Apple Pay, PayPal).
      // The payment succeeds, but PayPal SDK throws when cleanup runs on closed window.
      // Non-actionable - payment completed successfully, error is cosmetic.
      // Sentry issue: CHARITYSTACK-MONOREPO-ET
      'Window closed before response',
      /Window closed before response/,

      // PayPal SDK PostRobot/zoid cross-window communication errors
      // Occurs when PayPal SDK's zoid component is conditionally hidden (e.g., user switches
      // from one-time to recurring donation) but PostRobot still has pending async operations.
      // Also surfaces in parent windows (donation-elements, hosted-page) via cross-iframe messaging.
      // Non-actionable - PayPal SDK internal behavior, no user-facing impact.
      // Code mitigation already exists (paypalEverLoaded pattern in PaymentInfoPage.jsx).
      // Sentry issue: CHARITYSTACK-MONOREPO-H
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
      /Non-Error promise rejection captured with value: Object Not Found Matching Id/,

      // PayPal SDK PostRobot "Target window is closed" errors
      // Occurs when PayPal button container is replaced/removed from DOM or when users close
      // the PayPal popup modal before async operations complete. This is a notice from PayPal's
      // PostRobot communication library attempting to send messages to a closed window.
      // Common triggers: DOM updates during checkout, users closing PayPal modal prematurely.
      // Non-actionable - PayPal SDK internal behavior, no user-facing impact on payments.
      // Sentry issue: FCD-1374
      /Can not send postrobot_method.*Target window is closed/,
      'Can not send postrobot_method. Target window is closed',

      // PayPal SDK PostRobot enable() handshake timeout
      // Occurs when PayPal button iframe doesn't acknowledge the parent's enable()
      // message within 10 seconds — user closed tab, navigated away, or SDK was slow.
      // Non-actionable — PayPal SDK internal timeout, no user-facing payment impact.
      // Sentry issue: CHARITYSTACK-MONOREPO-1M
      /No ack for postMessage .+ in \d+ms/,

      // PayPal SDK zoid iframe load timeout
      // Thrown by zoid when the PayPal button iframe never fires `load` within 5s.
      // Observed after a *successful* donation: the donor closes the checkout from the
      // confirmation page, and DonorInfo.performExit() resets state back to a fresh
      // PAYMENT_INFO page ~200ms later. That remounts the express payment SDKs
      // (Stripe Express Checkout + <PayPalButtons>) into a container that is already
      // closing/hidden — and on iOS Safari often backgrounded — so neither iframe loads.
      // Stripe handles its own failure via onloaderror; zoid throws from a bare setTimeout
      // with no catch, so Sentry's browserApiErrors instrumentation reports it as unhandled.
      // Non-actionable — the payment is already captured before this fires.
      //
      // TRADEOFF: zoid throws this same string when PayPal buttons genuinely fail to render
      // on the payment page pre-donation (donor sees no PayPal button). Suppressing here
      // costs us that signal. If PayPal button render failures are ever reported, remove
      // this entry and fix the root cause in performExit instead (don't reset checkout
      // state to PAYMENT_INFO while the checkout is closed).
      // Sentry issue: CHARITYSTACK-MONOREPO-P4
      'Window did not load after 5000ms',
      /Window did not load after \d+ms/,

      // PostHog recorder script loading failures
      // CDN/network issues loading PostHog's session recording script.
      // Non-actionable - analytics only, not user-facing functionality.
      // Sentry issue: CHARITYSTACK-MONOREPO-ES
      /Unexpected end of script/,
      /SyntaxError.*posthog-recorder/,

      // Third-party IP lookup service failures (ipify.org)
      // External CDP script (cdp.js) calls api.ipify.org to resolve visitor IP.
      // Fetch fails due to ad blockers, network issues, or privacy extensions.
      // Non-actionable - third-party script behavior, not CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-FB
      /Failed to fetch.*ipify/i,

      // === Patterns added from Sentry triage session (2026-03-01) ===
      // Only genuinely non-actionable errors where we cannot fix the root cause
      // and the error has zero user impact. DO NOT add patterns here that could
      // mask real checkout failures, API issues, or infrastructure problems.

      // User-initiated request cancellations
      // User navigated away or cancelled a fetch before it completed.
      // Non-actionable — intentional user behavior, not a bug.
      // Sentry issue: CHARITYSTACK-MONOREPO-G3
      /AbortError/,
      'The user aborted a request.',

      // Browser ICU/Internationalization engine errors
      // Internal browser error in date/number formatting. Cannot be fixed in app code.
      // Sentry issue: CHARITYSTACK-MONOREPO-G4
      /Icu error/i,

      // Android/Instagram in-app browser postMessage failures
      // Java WebView bridge objects become garbage collected, causing postMessage
      // to fail with "Java object is gone". Non-actionable — platform limitation.
      // Sentry issue: CHARITYSTACK-MONOREPO-FT
      /Java object is gone/,
      /Error invoking postMessage.*Java object/,

      // Cross-origin SecurityError in restrictive browsers
      // Occurs in Firefox private browsing or when iframe sandbox blocks storage/cookies.
      // Non-actionable — browser privacy feature, not a bug.
      // Sentry issue: CHARITYSTACK-MONOREPO-FJ
      'SecurityError: The operation is insecure.',
      /The operation is insecure/,

      // Clipboard API unavailable in third-party tracking code
      // Stacktrace shows <anonymous>/getEventsTrackerCode — not our code.
      // Sentry issue: CHARITYSTACK-MONOREPO-G6
      /Cannot read properties of undefined \(reading 'writeText'\)/,

      // Mapbox API fetch failures (third-party service)
      // Network errors fetching map tiles/geocoding. Checkout still works without maps.
      // Sentry issue: CHARITYSTACK-MONOREPO-FM
      /Failed to fetch.*mapbox/i,

      // TikTok in-app browser evaluation errors (broadened from existing n.cid pattern)
      // Sentry issue: CHARITYSTACK-MONOREPO-FV
      /undefined is not an object \(evaluating 'i\.cid'\)/,

      // DOM manipulation errors from React HMR (development-only)
      // React Fast Refresh causes removeChild errors during hot reloads.
      // Filtered by beforeSend localhost check too, this is belt-and-suspenders.
      // Sentry issues: CHARITYSTACK-MONOREPO-G7, G8
      /Failed to execute 'removeChild' on 'Node'/,

      // Firefox cross-origin iframe errors from PostHog session recording
      // NS_ERROR_UNEXPECTED occurs when rrweb-record tries to access cross-origin iframe content
      // (payment processors: Stripe Elements, Finix, PayPal, Plaid).
      // Already suppressed in PostHog config (posthog-config.js:88-130) - adding here for Sentry.
      // Non-actionable - Firefox-specific browser security restriction.
      // Sentry issue: FCD-1173
      'NS_ERROR_UNEXPECTED',
      /NS_ERROR_UNEXPECTED/,

      // Cross-origin iframe security errors (all browsers)
      // These occur when PostHog/Sentry session recording tries to inspect payment iframes.
      // Mirrors PostHog's suppressibleErrors list (posthog-config.js:91-98).
      // Non-actionable - browser enforces same-origin policy on embedded payment processors.
      /SecurityError.*cross-origin/i,
      /Permission denied.*iframe/i,
      /Blocked a frame/i,
      /Failed to read.*cross-origin/i,

      // Matomo analytics javaEnabled proxy error
      // Occurs when browser extensions or privacy tools wrap navigator.javaEnabled in a Proxy.
      // The Proxy returns a wrapped function instead of the original, violating Proxy invariants
      // for read-only/non-configurable properties. Happens during page unload.
      // Non-actionable - third-party analytics script behavior, not CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-3W (FCD-1179)
      /property 'javaEnabled' is a read-only and non-configurable/,
      /'get' on proxy.*javaEnabled/,
      /matomo\.js/i,

      // Browser extension / WKWebView internal variable reference
      // "currentUrl" is not defined anywhere in our codebase (hosted scripts minify it away).
      // Occurs exclusively on iOS WKWebView — likely an injected script or browser internal.
      // Non-actionable — not our code.
      // Sentry issues: CHARITYSTACK-MONOREPO-GJ, GK
      "Can't find variable: currentUrl",

      // Stripe Express Checkout double-tap on iOS Safari
      // WebKit enforces a single PaymentRequest session per page. Fast double-taps on
      // Apple Pay button trigger a second session before the first resolves.
      // Originates inside Stripe's /clover/stripe.js — not our code.
      // A useRef guard in StripeExpressCheckout.jsx onClick reduces frequency (~95%),
      // but the remaining ~5% are still non-actionable.
      // Sentry issue: CHARITYSTACK-MONOREPO-2E
      'Page already has an active payment session',
      /InvalidAccessError.*active payment session/,

      // Safari date/time formatting errors from Intl.DateTimeFormat
      // Occurs when Intl.DateTimeFormat encounters invalid date strings or format patterns.
      // These are now handled with proper fallbacks in scheduleConfigUtils.js and
      // ControlledRecurringDatePicker.jsx, but we ignore them to prevent noise.
      // Anchored regex to avoid suppressing unrelated URL/pattern parsing errors.
      // Sentry issue: FCD-1331
      /^SyntaxError: The string did not match the expected pattern\.?$/,

      // Fingerprint.com SDK errors (FingerprintProvider is still active as of May 2026)
      // FingerprintProvider is wrapped in a Sentry.ErrorBoundary (donation-checkout/src/index.jsx)
      // that catches React render-time SDK failures (e.g. MIME type errors from ad blockers).
      // These ignoreErrors entries cover SDK errors that reach Sentry via the normal pipeline
      // (not React render-time throws, which are caught by the ErrorBoundary instead).
      // Common causes: missing API key, ad blockers, network issues, CSP restrictions.
      // Sentry issues: CHARITYSTACK-MONOREPO-JQ (script load), CHARITYSTACK-MONOREPO-JR (API key),
      //                CHARITYSTACK-MONOREPO-M3 (MIME type — handled by ErrorBoundary)
      /FingerprintError/,
      /^API key not found$/,
      'Failed to load the JS script of the agent',

      // iOS WebKit messageHandlers access without guard
      // Third-party scripts (payment SDKs, analytics, browser extensions) attempt to access
      // window.webkit.messageHandlers for iOS native bridge communication without checking existence.
      // Not our code - occurs in external dependencies or injected scripts.
      // Non-actionable - we cannot fix third-party script behavior.
      // Sentry issue: CHARITYSTACK-MONOREPO-WEBKIT (reported in Slack #alarms 2026-03-17)
      /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
      "undefined is not an object (evaluating 'window.webkit.messageHandlers')",

      // External embed script contentWindow.postMessage on iOS Safari
      // Occurs when the external CharityStack embed script (bundle.js) tries to
      // postMessage to an iframe that has been removed from the DOM. Variable name
      // varies between minified builds (n, i, t, etc.).
      // Non-actionable — external script behavior on iOS, no user-facing impact.
      // Sentry issue: CHARITYSTACK-MONOREPO-KM
      /undefined is not an object \(evaluating '.*\.contentWindow\.postMessage'\)/,

      // Synchronous XHR during page dismissal (Chrome 80+ deprecation)
      // Chrome blocks synchronous XMLHttpRequest during beforeunload/unload/pagehide events.
      // Third-party scripts (Finix SDK, external CharityStack hosted script) may attempt sync XHR
      // during page dismissal — this is non-actionable since we don't control the external code,
      // and the user is already navigating away.
      // Sentry issue: CHARITYSTACK-MONOREPO-JK
      /Synchronous XHR in page dismissal/,
      // Browser IndexedDB connection lost errors
      // Occurs when the browser's IndexedDB storage process crashes, becomes unresponsive,
      // or is terminated by the OS (common on iOS Safari, mobile browsers with memory pressure,
      // and in-app browsers). PostHog uses IndexedDB as its default persistence mechanism
      // for hosted-page (donation-elements uses sessionStorage instead).
      // Non-actionable — browser-level storage failure, not caused by CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-JX
      /Connection to Indexed Database server lost/,
      'UnknownError: Connection to Indexed Database server lost. Refresh the page to try again',
      // Browser storage quota estimate failure (navigator.storage.estimate)
      // Same family as the IndexedDB connection loss above — browser storage layer
      // failure on mobile Safari / in-app browsers during SDK persistence checks.
      // Non-actionable — browser-level storage failure, not caused by CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-MF
      /Unknown error occurred while getting estimate/,
      // sessionStorage/localStorage access denied in restrictive browser contexts
      // Third-party scripts (e.g., openpixel/grpixel tracking pixel) access sessionStorage
      // without try-catch. Chrome throws SecurityError when storage is blocked by privacy
      // settings, extensions, or "Block third-party cookies" mode.
      // Non-actionable - third-party script behavior, not CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-JM (FCD-1380)
      /Failed to read the 'sessionStorage' property from 'Window': Access is denied/,

      // External CharityStack script (bundle.js) build-time placeholder not replaced
      // The hosted-page loads an external script via VITE_CS_JS_SCRIPT for checkout iframe
      // management. That script uses __SCRIPT_SOURCE_PLACEHOLDER__ as a build-time token
      // that should be replaced during its own build. When the replacement fails, it throws
      // a ReferenceError. Non-actionable from monorepo — fix is in the external script build.
      // Sentry issue: CHARITYSTACK-MONOREPO-K3
      "Can't find variable: __SCRIPT_SOURCE_PLACEHOLDER__",
      /Can't find variable: __SCRIPT_SOURCE_PLACEHOLDER__/,
      /__SCRIPT_SOURCE_PLACEHOLDER__ is not defined/,
      // /Failed to read the 'localStorage' property from 'Window': Access is denied/,
      // ^ Uncomment and add Sentry reference if localStorage errors from openpixel/grpixel are confirmed
      // Stripe Element destroyed race condition (belt-and-suspenders)
      // Stripe throws IntegrationError internally when an Element (Card, ExpressCheckout) is
      // destroyed mid-operation — e.g., amount changes cause <Elements> to recreate while the
      // Apple Pay / Google Pay confirmation sheet is open. Our code now catches this in
      // StripeExpressCheckout.onConfirm and StripeCheckout.handleSubmit, but Stripe may also
      // throw in its own internal event listeners before our handlers run.
      // Non-actionable from Sentry's perspective — user sees a retry prompt.
      // Sentry issue: CHARITYSTACK-MONOREPO-K1
      'This Element has already been destroyed',
      /IntegrationError.*Element.*destroyed/,

      // PostHog rrweb lazy-recorder Web Worker creation failure
      // PostHog's rrweb session recording module (loaded from CDN: us-assets.i.posthog.com)
      // creates a Web Worker for canvas snapshot processing. The Worker constructor is called
      // without a typeof guard, so it throws ReferenceError in environments where Worker is
      // undefined: in-app browsers (Instagram, TikTok, Facebook), restrictive iframe sandboxes,
      // and older Android WebViews. recordCanvas:false is already set in posthog-config.js but
      // rrweb still initializes the Worker proactively. Upstream PostHog/rrweb bug.
      // Non-actionable — analytics-only, zero user-facing impact. Session recording degrades
      // gracefully without canvas snapshots.
      // Sentry issue: CHARITYSTACK-MONOREPO-K4
      'Worker is not defined',
      /ReferenceError.*Worker is not defined/,
      "Can't find variable: Worker",  // Safari/WebKit variant

      // User-facing payment decline messages (donor card errors, not bugs)
      // handlePaymentError() in paymentErrors.js sends Sentry.captureException for all
      // payment errors including expected declines (bad CVC, expired card, insufficient
      // funds, etc.). These are user errors, not actionable from engineering.
      // Sentry issues: CHARITYSTACK-MONOREPO-KC, CHARITYSTACK-MONOREPO-KF
      /Payment Declined/i,
      /Security code doesn't match/i,
      /card couldn't be processed/i,
      /card has expired/i,
      /Insufficient funds/i,
      /bank declined this transaction/i,
      /Invalid card number/i,
      /zip code does not match/i,
      /flagged for security/i,
      /card cannot be used/i,

      // Browser extension injected scripts (not CharityStack code)
      // Extensions inject content scripts with obfuscated function names like
      // self.<random-hash>. These execute on HTMLDocument from anonymous scripts
      // with no CharityStack breadcrumbs.
      // Sentry issue: CHARITYSTACK-MONOREPO-KD
      /self\.\w{10,} is not a function/,

      // PostHog Blob API unavailable in restricted browser environments
      // PostHog uses Blob internally for feature flag persistence. In sandboxed iframes,
      // headless browsers, and some Linux/Chrome 116 environments, Blob is undefined.
      // PostHog's async init (inside a setTimeout) throws before our try-catch can intercept.
      // A typeof guard in index.jsx prevents init, but this pattern catches any residual throws.
      // Non-actionable — browser environment limitation, analytics-only impact.
      // Sentry issue: CHARITYSTACK-MONOREPO-5M
      'Blob is not a constructor',
    ],

    // Block errors from specific domains (external scripts not controlled by CharityStack)
    // These scripts run on the parent page when donation-elements is embedded.
    denyUrls: [
      // External CDP (Customer Data Platform) scripts from client websites
      // These are third-party analytics scripts injected by nonprofits, not CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-EM (visitor.id error from analyticsup.site)
      /analyticsup\.site/i,
      /cdn\.analyticsup\.site/i,

      // Google Tag Manager and related scripts (inject third-party code)
      /googletagmanager\.com/i,

      // Common third-party analytics that may throw errors
      /hotjar\.com/i,
      /clarity\.ms/i,

      // Google reCAPTCHA internal errors
      // Errors from recaptcha__en.js on outdated browsers (e.g., Chrome Mobile 51, Android 6.0.1).
      // These are Google script internals failing on unsupported browsers.
      // Sentry issue: CHARITYSTACK-MONOREPO-EV
      /recaptcha.*\.js/i,
      /gstatic\.com.*recaptcha/i,
      /google\.com.*recaptcha/i,

      // PostHog CDN scripts (analytics, non-critical)
      // Syntax errors from incomplete script loads due to network issues.
      // Sentry issue: CHARITYSTACK-MONOREPO-ES
      /posthog-recorder\.js/i,
      /us-assets\.i\.posthog\.com/i,

      // PostHog scripts served through OUR reverse proxy (s.charitystack.com).
      // Reverse-proxying a third party onto our own domain defeats both of the
      // filters above: PostHog's code arrives from a CharityStack hostname, so
      // `us-assets.i.posthog.com` never matches and Sentry's in-app heuristic
      // marks the frame `inApp: true` — a non-actionable third-party parse error
      // then pages as a HIGH-priority first-party one. Any PostHog lazy-loaded
      // module (surveys.js, recorder, future ones) lands under `/static/` on the
      // proxy. `/static/` is NEVER CharityStack code: no package overrides Vite's
      // `assetsDir`, so our own bundles always live under `/assets/`.
      //
      // Deliberately URL-scoped, NOT message-scoped. Do not add a bare
      // `SyntaxError: Unexpected EOF` to ignoreErrors — a truncated CharityStack
      // chunk throws the identical message and IS actionable (CDN/deploy bug).
      // Note the wording gap that let CHARITYSTACK-MONOREPO-PF past the
      // CHARITYSTACK-MONOREPO-ES filter: Chrome/Firefox say "Unexpected end of
      // script" (matched at ignoreErrors above), Safari says "Unexpected EOF".
      // Sentry issue: CHARITYSTACK-MONOREPO-PF (PE-2116)
      /s\.charitystack\.com\/static\//i,
      /\/static\/surveys\.js/i,

      // Third-party CDP (Customer Data Platform) script served from same domain
      // This script (/assets/js/cdp.js) is NOT part of the CharityStack monorepo build.
      // It makes unprotected fetch calls to api.ipify.org that fail in restrictive environments.
      // Sentry issue: CHARITYSTACK-MONOREPO-FB
      /\/assets\/js\/cdp\.js/i,

      // Sentry's own internal scripts (not our code at all)
      // Sentry issue: CHARITYSTACK-MONOREPO-G5
      /sentry\.io\/_static/i,

      // Matomo analytics scripts (third-party analytics)
      // Errors from Matomo tracking scripts (e.g., javaEnabled proxy errors).
      // Sentry issue: CHARITYSTACK-MONOREPO-3W (FCD-1179)
      /matomo\.js/i,
      /matomo.*\.js$/i,

      // Google reCAPTCHA internal scripts (/web/v4/{hash} paths)
      // reCAPTCHA v3 loads additional internal scripts from Google's CDN at paths
      // like /web/v4/URjy0myD1xDSXk1PLSI4 that don't contain "recaptcha" in the URL.
      // These scripts throw TypeErrors (e.g., "Cannot read properties of null (reading '')")
      // when DOM elements are manipulated during processing, browser extensions interfere,
      // or the page navigates while reCAPTCHA is active.
      // Non-actionable — Google reCAPTCHA internal code, not CharityStack code.
      // Sentry issue: CHARITYSTACK-MONOREPO-K2
      /gstatic\.com.*\/web\/v\d+\//i,
      // Open Pixel (openpixel/grpixel) tracking pixel
      // Third-party analytics pixel loaded by nonprofits. Accesses sessionStorage without
      // error handling, causing SecurityError in restrictive browser contexts.
      // Sentry issue: CHARITYSTACK-MONOREPO-JM (FCD-1380)
      /openpixel/i,
      /grpixel/i,
    ],

    // Sample rates - higher for revenue-critical apps
    // checkout: 20% (critical payment flow)
    // others: 10% (standard monitoring)
    tracesSampleRate: appName === 'donation-checkout' ? 0.2 : 0.1,
    profilesSampleRate: appName === 'donation-checkout' ? 0.2 : 0.1,

    // Enable console logs to be sent to Sentry
    enableLogs: true,

    integrations: [
      // Browser tracing for navigation and performance monitoring
      options.browserTracingIntegration?.({
        tracingOrigins: ['localhost', /^\//],
      }),

      // Session replay with cross-origin iframe blocking
      // Note: donation-elements does NOT initialize replayIntegration (privacy in cross-origin iframes)
      options.replayIntegration?.({
        block: ['iframe'], // Prevent cross-origin security errors

        // IMPORTANT: maskAllInputs is intentionally FALSE to prevent a production crash.
        //
        // When true, Sentry Replay patches property setters on ALL form elements
        // (<select>, <option>, <input>, etc.) and defers updates via setTimeout.
        // Third-party browser extensions can interfere with these deferred callbacks,
        // causing: TypeError: Right-hand side of 'instanceof' is not an object
        // This error propagates through React reconciliation and triggers
        // CheckoutErrorBoundary, crashing the checkout UI for users.
        //
        // PCI safety: Card/bank data is ALREADY protected by payment provider iframes
        // (Stripe Elements, Finix SDK, Plaid) — Sentry cannot access cross-origin
        // iframe content regardless of this setting. Sensitive PII inputs (email, phone)
        // are selectively masked via data-sentry-mask attributes on the components.
        maskAllInputs: false,

        // Debugging: maskAllText=false allows seeing error messages, buttons, labels
        // We use selective masking for sensitive text elements instead
        maskAllText: false,

        blockAllMedia: true, // Block images/videos for privacy

        // Selective masking: text content + specific PII inputs
        mask: [
          // Text content masking
          '.donor-name',           // Mask donor names
          '.donation-amount',      // Mask donation amounts
          '.email-display',        // Mask email addresses
          '.address-text',         // Mask physical addresses
          '.phone-number',         // Mask phone numbers
          '[data-sensitive]',      // Any element marked as sensitive

          // Input value masking (PII protection)
          // Card/bank data lives in payment provider iframes (already inaccessible).
          // These selectors cover PII inputs rendered directly in our DOM.
          '[data-sentry-mask]',    // Explicit opt-in (added to email/phone inputs)
          'input[type="password"]', // Password fields (e.g., dashboard login)
        ],
      }),

      // Console logging integration - only capture console.error to reduce noise
      // Note: console.log and console.warn filtered out to avoid PostHog debug spam
      options.consoleLoggingIntegration?.({
        levels: ['error'],
      }),
    ].filter(Boolean),

    // Session replay sample rates
    // donation-elements: 0% (cross-origin iframe privacy concerns)
    // others: 10% normal sessions, 100% error sessions
    replaysSessionSampleRate: appName === 'donation-elements' ? 0 : 0.1,
    replaysOnErrorSampleRate: appName === 'donation-elements' ? 0 : 1.0,

    // Initial scope with app and environment tags
    initialScope: {
      tags: {
        app: appName,
        environment: import.meta.env.VITE_APP_ENVIRONMENT,
      },
    },

    // Enrich events with additional context before sending
    // This function is called for EVERY error - must be lightweight and fast
    beforeSend(event) {
      // Drop errors from localhost/development environments
      // Belt-and-suspenders: `enabled: !isDevelopment` should prevent this,
      // but if VITE_APP_ENVIRONMENT is misconfigured, this catches it.
      try {
        const url = event.request?.url || window.location.href;
        if (url.includes('localhost') || url.includes('127.0.0.1')) {
          return null;
        }
      } catch (e) {
        // Ignore - URL check is best-effort
      }

      // Drop bot events silently to reduce Sentry noise and quota usage.
      // Signals collected once at init by collectBotSignals(); scoring is ~0ms.
      // Sentry context: CHARITYSTACK-MONOREPO-JS had ~97% bot traffic.
      if (isBotLikely()) {
        return null;
      }

      // Drop Stripe Connect account_sessions/claim errors on iOS only.
      // Connect.js iframes fail on iOS (WKWebView unsupported, Safari ITP interference).
      // On desktop, keep these — they'd indicate a real backend issue.
      // Sentry: CHARITYSTACK-MONOREPO-AV, KQ, KR, KS, KN, KP, KG, KV
      try {
        const errorMsg = event.exception?.values?.[0]?.value || '';
        if (
          /iPhone|iPad|iPod/.test(navigator.userAgent) &&
          errorMsg.includes('No account session with that client secret')
        ) {
          return null;
        }
      } catch (e) {
        // Best-effort — don't block other events on filter failure
      }

      // Drop React HMR / Fast Refresh errors (dev-only, non-actionable)
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const isHMRError = frames.some(
        (f) => f.filename?.includes('@react-refresh') || f.filename?.includes('/@vite/'),
      );
      if (isHMRError) {
        return null;
      }

      // Filter PostHog internal debug logs (high volume, low value)
      if (event.message?.includes('[PostHog.js]')) {
        return null; // Don't send to Sentry
      }

      // Filter browser automation logs (internal browser extension noise)
      if (event.message?.includes('[BrowserAutomation]')) {
        return null;
      }

      // Filter React development warnings in production
      if (event.message?.startsWith('Warning:') && event.level === 'warning') {
        return null;
      }

      // Link to PostHog session for cross-tool correlation
      // Read fresh value to ensure accuracy when PostHog session changes
      // Performance: sessionStorage.getItem() is ~0.01ms (negligible overhead)
      try {
        const posthogSession = sessionStorage.getItem('charitystack_parent_session');
        if (posthogSession) {
          event.tags = event.tags || {};
          event.tags.posthog_session = posthogSession;
        }
      } catch (e) {
        // sessionStorage may fail in cross-origin contexts or if disabled
        // This is expected and non-critical - session linking just won't work
      }

      // Sample warning-level events at lower rate (10%) to reduce noise
      if (event.level === 'warning' && Math.random() > 0.1) {
        return null; // Don't send this event
      }

      return event;
    },
  };
};

/**
 * Set flow context for cross-app tracking
 * Called before navigating from one app to another (e.g., elements → checkout)
 *
 * IMPORTANT - Cross-Origin Limitation:
 * - This function uses sessionStorage which FAILS in cross-origin iframes (donation-elements)
 * - donation-elements is embedded on external nonprofit websites (95%+ cross-origin)
 * - For elements→checkout flow, the external CharityStack script MUST pass flow context via URL params
 * - URL parameters take priority over sessionStorage in getFlowContext()
 *
 * @param {string} flowSource - Source of the flow (elements, hosted)
 * @param {string} formId - Form ID associated with the flow
 * @param {object} additionalContext - Additional context to store
 * @returns {boolean} True if sessionStorage write succeeded, false if failed (expected for cross-origin)
 *
 * Example usage:
 * setFlowContext('elements', 'abc-123', { amount: 50, frequency: 'monthly' })
 *
 * External script implementation (for donation-elements → checkout):
 * const checkoutUrl = `/checkout/?formID=${formId}&sentryFlow=elements&sentryFormId=${formId}`;
 */
export const setFlowContext = (flowSource, formId, additionalContext = {}) => {
  try {
    // Store in sessionStorage (works same-origin only)
    sessionStorage.setItem('sentry_flow_source', flowSource);
    sessionStorage.setItem('sentry_form_id', formId);

    // Store additional context
    Object.keys(additionalContext).forEach(key => {
      const value = additionalContext[key];
      if (value !== null && value !== undefined) {
        sessionStorage.setItem(`sentry_${key}`, String(value));
      }
    });

    return true; // Success
  } catch (e) {
    // sessionStorage unavailable in:
    // - Cross-origin iframes (SecurityError - EXPECTED for donation-elements)
    // - Private browsing mode
    // - Storage quota exceeded

    if (flowSource === 'elements') {
      // Expected failure - external script should use URL params instead
      console.log('[Sentry] Cross-origin context detected - external script must pass sentryFlow/sentryFormId URL params');
    } else {
      console.warn('[Sentry] Could not set flow context in sessionStorage:', e);
    }

    return false; // Failure
  }
};

/**
 * Get flow context for reading in destination app
 * Called when initializing Sentry in the destination app (e.g., checkout)
 *
 * Triple-fallback priority (mirrors PostHog pattern):
 * 1. URL parameters (sentryFlow, sentryFormId) - REQUIRED for cross-origin elements→checkout
 * 2. sessionStorage (sentry_flow_source, sentry_form_id) - Works for hosted→checkout same-origin
 * 3. Default values (direct, unknown)
 *
 * @returns {object} Flow context with flowSource and formId
 *
 * Example return value:
 * { flowSource: 'elements', formId: 'abc-123' }
 */
export const getFlowContext = () => {
  // Priority 1: URL parameters (required for cross-origin scenarios)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFlowSource = urlParams.get('sentryFlow');
    const urlFormId = urlParams.get('sentryFormId');

    if (urlFlowSource && urlFormId) {
      console.log('[Sentry] Flow context from URL params:', { flowSource: urlFlowSource, formId: urlFormId });
      return {
        flowSource: urlFlowSource,
        formId: urlFormId,
      };
    }
  } catch (e) {
    console.warn('[Sentry] Could not read URL parameters:', e);
  }

  // Priority 2: sessionStorage (works same-origin only)
  try {
    const storageFlowSource = sessionStorage.getItem('sentry_flow_source');
    const storageFormId = sessionStorage.getItem('sentry_form_id');

    if (storageFlowSource && storageFormId) {
      console.log('[Sentry] Flow context from sessionStorage:', { flowSource: storageFlowSource, formId: storageFormId });
      return {
        flowSource: storageFlowSource,
        formId: storageFormId,
      };
    }
  } catch (e) {
    console.warn('[Sentry] Could not read sessionStorage:', e);
  }

  // Priority 3: Default values
  console.log('[Sentry] No flow context found - using defaults');
  return {
    flowSource: 'direct',
    formId: 'unknown',
  };
};
